import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { getUserProfile, OutputType as ProfileType } from '../endpoints/user/profile_GET.schema';
import {
  postUserProfile,
  InputType as UpdateProfileInput,
} from '../endpoints/user/profile_POST.schema';
import {
  postChangePassword,
  InputType as ChangePasswordInput,
} from '../endpoints/user/change-password_POST.schema';
import {
  postSetPassword,
  InputType as SetPasswordInput,
} from '../endpoints/user/set-password_POST.schema';
import { AUTH_QUERY_KEY } from './useAuth';

export const USER_PROFILE_QUERY_KEY = ['user', 'profile'] as const;

export function useUserProfile() {
  return useQuery({
    queryKey: USER_PROFILE_QUERY_KEY,
    queryFn: () => getUserProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileInput) => postUserProfile(data),
    onSuccess: (updatedUser) => {
      // Update the profile query data
      queryClient.setQueryData(USER_PROFILE_QUERY_KEY, updatedUser);

      // Also update the auth session data since it contains user info like displayName/avatar
      queryClient.setQueryData(AUTH_QUERY_KEY, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          displayName: updatedUser.displayName,
          avatarUrl: updatedUser.avatarUrl,
        };
      });

      // Invalidate to be safe
      queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordInput) => postChangePassword(data),
  });
}

export function useSetPassword() {
  return useMutation({
    mutationFn: (data: SetPasswordInput) => postSetPassword(data),
  });
}
